import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { combineLatest, delay, map, Observable, of, share, switchMap, take, tap } from 'rxjs';

import { retryWithBackoff } from '@fcom/rx';
import { ConfigService } from '@fcom/core/services/config/config.service';
import { BookingAppState } from '@fcom/common/interfaces/booking/booking-state.interface';
import { FinnairAirBoundsRequest } from '@fcom/dapi/api/models/finnair-air-bounds-request';
import { FinnairAirBoundsRequestItinerary } from '@fcom/dapi/api/models/finnair-air-bounds-request-itinerary';
import { FinnairAirBoundsRequestTravelers } from '@fcom/dapi/api/models/finnair-air-bounds-request-travelers';
import { FinnairAirBoundsResponse } from '@fcom/dapi/api/models/finnair-air-bounds-response';
import { OfferListFetchParams } from '@fcom/common/interfaces/offer-list-fetch-params';
import { stringHashCode } from '@fcom/core/utils/utils';
import { LoginStatus } from '@fcom/core-api/login/interfaces/login.interface';
import { DapiService } from '@fcom/dapi/api/dapi.service';
import { airCalendarPriceMatchSelector } from '@fcom/booking-widget/store/selectors/booking-widget.selector';

import { findAirBounds } from '../../../dapi/api/fn/offers/find-air-bounds';
import * as BoundsActions from '../../store/actions/bounds.actions';
import { outboundCache } from '../../store/selectors/bounds.selector';
import { instantSearchResultMatchSelector } from '../../store/selectors/instant-search.selectors';

export interface PreSelectionParams {
  departureFlightNumbers?: string;
  returnFlightNumbers?: string;
  departureFareFamily?: string;
  returnFareFamily?: string;
}

export type TicketSelectionParams = OfferListFetchParams & PreSelectionParams;

@Injectable()
export class CommonAirBoundsService {
  private configService = inject(ConfigService);
  private store$ = inject<Store<BookingAppState>>(Store);
  private dapiService = inject(DapiService);
  static NUMBER_OF_RETRIES = 1;

  constructor() {
    /* noop */
  }

  fetchAndStoreOutboundsToCache(
    params: TicketSelectionParams,
    loginStatus: LoginStatus
  ): Observable<FinnairAirBoundsResponse> {
    const currentParamsSerialized = JSON.stringify({ params, loginStatus });
    const outboundCacheHash = String(stringHashCode(currentParamsSerialized));
    return this.store$.pipe(outboundCache(outboundCacheHash, Date.now())).pipe(
      take(1),
      switchMap((cache) => {
        if (cache) {
          return of(cache).pipe(delay(0));
        }

        return this.withMonitoringPrices(this.createRequest(params)).pipe(
          switchMap((bodyParams) => this.getBounds(bodyParams)),
          retryWithBackoff(CommonAirBoundsService.NUMBER_OF_RETRIES),
          tap((bounds) => {
            if (!bounds) {
              return;
            }
            this.store$.dispatch(
              BoundsActions.setOutboundCache({
                hash: outboundCacheHash,
                createdAt: Date.now(),
                bounds,
              })
            );
          })
        );
      })
    );
  }

  /**
   * Instant search and air calendar prices are whole-trip prices, so they only correspond to requests for the final bound.
   */
  withMonitoringPrices(request: FinnairAirBoundsRequest): Observable<FinnairAirBoundsRequest> {
    if (!request.itineraries.at(-1)?.isRequestedBound) {
      return of(request);
    }

    return combineLatest([
      this.store$.select(instantSearchResultMatchSelector(request)),
      this.store$.select(airCalendarPriceMatchSelector(request)),
    ]).pipe(
      take(1),
      map(([instantSearchMatch, airCalendarPrice]) => ({
        ...request,
        ...(instantSearchMatch?.price ? { instantSearchPrice: String(instantSearchMatch.price) } : {}),
        ...(airCalendarPrice ? { airCalendarPrice } : {}),
      }))
    );
  }

  /**
   * Get flight bounds for a certain date.
   *
   * @param params the search parameters to use
   * @returns {Observable<FinnairAirBoundsResponse>} the fetched bounds
   */
  private getBounds(params: FinnairAirBoundsRequest): Observable<FinnairAirBoundsResponse> {
    return this.dapiService
      .invoke$(findAirBounds, this.configService.cfg.offersUrl, {
        body: params,
      })
      .pipe(share());
  }

  /**
   * Create air bounds request from query parameters.
   *
   * @param params the search params used to create the request
   * @param params.locale the locale to send with the request
   * @param params.origin the IATA code of the destination to fly to
   * @param params.destination the IATA code of the departure location
   * @param params.directFlights
   * @param params.cabin the travel class to query
   * @param params.paxAmount passenger amounts
   * @param params.departureDate the date of departure
   * @param params.returnDate the date of return, leave undefined for one-way trips
   * @param params.promoCode given discount code to apply
   * @returns {FinnairAirBoundsRequest} the request object to use
   */
  private createRequest(params: OfferListFetchParams): FinnairAirBoundsRequest {
    const { cabin, locale, paxAmount, promoCode } = params;
    const itineraries = this.buildItineraries(params);
    return {
      locale,
      cabin,
      travelers: {
        adults: paxAmount.adults,
        children: paxAmount.children,
        c15s: paxAmount.c15s,
        infants: paxAmount.infants,
      } as unknown as FinnairAirBoundsRequestTravelers,
      itineraries,
      promoCode,
    };
  }

  private buildItineraries(params: OfferListFetchParams): FinnairAirBoundsRequestItinerary[] {
    const { directFlights, flights } = params;
    return flights.map((f, index) => ({
      directFlights,
      departureLocationCode: f.origin,
      destinationLocationCode: f.destination,
      departureDate: f.departureDate.toString(),
      isRequestedBound: index === 0,
    }));
  }
}
